import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'store/store-hooks';
import { useAudioTrackContext } from './_mediator/audio-track.provider';
import { TimeLabel, TimeLabelPayload } from 'components/app/app-components/time-label/time-label';
import styles from './_audio-track.module.scss';
import { EnumDragger } from '../_mediator/app.slice';

interface Props {
  ws: WaveSurfer;
  isOver: boolean;
}

export const WsTimeLabel = ({ ws, isOver }: Props) => {
  const { wsGetCursorTime, wsGetCursorPosition, wsGetCursorFraction, leftOffset } = useAudioTrackContext();
  const [payload, setPayload] = useState<TimeLabelPayload>({ cssPosition: '', time: 0 });
  const dragger = useAppSelector((state) => state.app.dragger);

  const isDragger = useMemo(() => {
    return dragger !== null && [EnumDragger.ws, EnumDragger.cell].includes(dragger);
  }, [dragger]);

  const isActive = isDragger || isOver;

  useEffect(() => {
    if (!isDragger) return;
    const onSeek = () => {
      setPayload({
        cssPosition: wsGetCursorPosition(wsGetCursorTime('fraction')) + 'px',
        time: wsGetCursorTime(),
      });
    };
    onSeek();
    ws.on('seek', onSeek);
    return () => ws.un('seek', onSeek);
  }, [isDragger]);

  useEffect(() => {
    if (!isOver || isDragger) return;
    const duration = ws.getDuration();
    const cb = (event: MouseEvent) => {
      setPayload({
        cssPosition: event.clientX - leftOffset.current + 'px',
        time: wsGetCursorFraction(event.clientX) * duration,
      });
    };
    document.addEventListener('mousemove', cb);
    return () => document.removeEventListener('mousemove', cb);
  }, [isOver]);

  return <TimeLabel className={styles.timeLabel} payload={payload} enable={isActive} invert={isDragger} />;
};
